import React from 'react';
import { FileText, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';

const ImportHistory = ({ history, onRefresh }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Import History</h2>
        <button
          onClick={onRefresh}
          className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>
      {history?.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No import history yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {history?.map((record) => (
            <div key={record?.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(record?.status)}
                  <div>
                    <h3 className="font-semibold text-gray-900">{record?.fileName}</h3>
                    <p className="text-sm text-gray-500">
                      {new Date(record?.createdAt)?.toLocaleString()}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  getStatusColor(record?.status)
                }`}>
                  {record?.status}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Total Rows</p>
                  <p className="text-2xl font-bold text-gray-900">{record?.totalRows}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{record?.successfulImports}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Duplicates</p>
                  <p className="text-2xl font-bold text-yellow-600">{record?.duplicatesFound}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{record?.failedImports}</p>
                </div>
              </div>

              {record?.errorLog?.length > 0 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    View Error Details ({record?.errorLog?.length} errors)
                  </summary>
                  <div className="mt-3 max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-4">
                    {record?.errorLog?.map((err, index) => (
                      <div key={index} className="mb-2 text-sm">
                        <span className="font-medium text-gray-700">Row {err?.row}:</span>
                        <span className="text-red-600 ml-2">{err?.errors?.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportHistory;